import { z } from 'zod';

/** The four dials every intervention exerts onto the entity response model. */
export const LEVER_KEYS = ['efficiency', 'resilience', 'growth', 'diversification'] as const;
export type LeverKey = (typeof LEVER_KEYS)[number];

export const zLevers = z.object({
  efficiency: z.number().min(0).max(1),
  resilience: z.number().min(0).max(1),
  growth: z.number().min(0).max(1),
  diversification: z.number().min(0).max(1),
});
export type Levers = z.infer<typeof zLevers>;

export const LEVER_META: Record<LeverKey, { label: string; q: string }> = {
  efficiency:      { label: 'Resource efficiency',   q: 'Does it cut the resources, energy or materials needed per unit of value?' },
  resilience:      { label: 'Resilience buffers',    q: 'Does it add reserves, redundancy or shock absorption?' },
  growth:          { label: 'Growth aggressiveness', q: 'Does it expand capacity, markets or output?' },
  diversification: { label: 'Diversification',       q: 'Does it spread dependencies, markets or bets?' },
};

/** UI levels for the plain-language profile controls. */
export const LEVER_LEVELS = [
  { key: 'none',   label: 'None',   v: 0 },
  { key: 'slight', label: 'Slight', v: 0.3 },
  { key: 'strong', label: 'Strong', v: 0.6 },
  { key: 'core',   label: 'Core',   v: 0.9 },
] as const;

export function blankLevers(): Levers {
  return { efficiency: 0, resilience: 0, growth: 0, diversification: 0 };
}

export function copyLevers(src?: Partial<Levers> | null): Levers {
  const out = blankLevers();
  for (const k of LEVER_KEYS) out[k] = Math.max(0, Math.min(1, src?.[k] ?? 0));
  return out;
}

/** Stable, compact key for a lever vector — used for caching. */
export function leverFingerprint(levers?: Partial<Levers> | null): string {
  return LEVER_KEYS.map(k => k[0] + Math.round(((levers?.[k]) ?? 0) * 100)).join('');
}

/** Cosine similarity between two lever vectors (0 if either is empty). */
export function leverSimilarity(a: Partial<Levers>, b: Partial<Levers>): number {
  let dot = 0, na = 0, nb = 0;
  for (const k of LEVER_KEYS) {
    const x = a[k] ?? 0, y = b[k] ?? 0;
    dot += x * y; na += x * x; nb += y * y;
  }
  return na && nb ? dot / Math.sqrt(na * nb) : 0;
}
